import { HvlAbstractLazyList, HvlFormConstants } from '@hvlng/framework-core/form';
import { HvlBaseModel, HvlPage } from '@hvlng/framework-bff/core';
import { LazyLoadEvent } from 'primeng/api';
import { Directive, Injector, Input } from '@angular/core';
import { Router } from '@angular/router';
import { logComponent } from '../hr-logger';
import { HrFormLoadingVisibility } from './hr-form-loading-visibility';
import { getFormTitleAsync, getFormTitleSync, goMasterData, translateAsync } from '../page-code/page-code-func';
import { TranslateService } from '@ngx-translate/core';
import { UiConfirmationDialogService, UiToastMessageService } from 'ui-shared';
import { ServiceCall } from '../service/service-call';
import { HvlLanguageService } from '@hvlng/framework-core/language';
import { HrBaseParam } from './hr-base-param';
import { HrTranslateKey } from '../translate/hr-translate-key';
import { PageCodeModel } from '../page-code/page-code-model';

@Directive()
export abstract class HrBaseListPageNew<T extends HvlBaseModel>
    extends HvlAbstractLazyList<T>
    implements HrFormLoadingVisibility {
    @Input() listTitle: string;
    @Input() pageCodeModel: PageCodeModel;

    private _tableList: T[];
    private _selectedItem: T;
    private _tableEvent: LazyLoadEvent;
    private _searchTitle: string;

    protected translateService: TranslateService;
    public toastMessage: UiToastMessageService;
    public serviceCall: ServiceCall;
    public confirmationDialogService: UiConfirmationDialogService;
    public langService: HvlLanguageService;
    public router?: Router;

    protected constructor(injector: Injector, params?: HrBaseParam) {
        super();
        logComponent(this);
        this.initVariables(injector, params);
    }

    private initVariables(injector: Injector, params?: HrBaseParam) {
        this.translateService = injector.get(TranslateService);
        this.toastMessage = injector.get(UiToastMessageService);
        this.serviceCall = injector.get(ServiceCall);
        this.confirmationDialogService = injector.get(UiConfirmationDialogService);
        this.langService = injector.get(HvlLanguageService);
        if (params?.hasRouter) {
            this.router = injector.get(Router);
        }
    }

    get tableEvent(): LazyLoadEvent {
        return this._tableEvent;
    }

    set tableEvent(event: LazyLoadEvent) {
        this._tableEvent = event;
    }

    public loadTableEvent(event: LazyLoadEvent) {
        this._tableEvent = event;
    }

    get tableList(): T[] {
        return this._tableList;
    }

    public setTableList(list: T[]) {
        this._tableList = list;
    }

    public clearTableList() {
        this._tableList = [];
    }

    get selectedItem(): T {
        return this._selectedItem;
    }

    set selectedItem(value: T) {
        this._selectedItem = value;
    }

    public clearSelectedItem() {
        this._selectedItem = null;
    }

    get searchTitle(): string {
        return this._searchTitle;
    }

    set searchTitle(value: string) {
        this._searchTitle = value;
    }

    public showLoading() {
        this.loading = true;
    }

    public hideLoading() {
        this.loading = false;
    }

    public clearTableData() {
        this.clearTableList();
        this.totalElements = 0;
        this.totalPages = 0;
        this.refreshTable();
    }

    public setTableData(response: HvlPage<T[]>) {
        this.setTableList(response.data);
        this.totalElements = response.totalElements;
        this.totalPages = response.totalPages;
    }

    public goMasterData() {
        goMasterData(this.router);
    }

    onRowSelect() {
        this.onRowSelectEventEmitter.emit(this.selectedItem);
    }

    onRowUnSelect() {
        this.onRowUnSelectEventEmitter.emit(null);
    }

    public get formModeEnum(): typeof HvlFormConstants {
        return HvlFormConstants;
    }

    public get hrKey(): typeof HrTranslateKey {
        return HrTranslateKey;
    }

    public translateSync(key: string): string | any {
        return this.translateService.instant(key);
    }

    public translateAsync(key: string): Promise<string | any> {
        return translateAsync(this.translateService, key);
    }

    public async getFormTitleAsyncAsSearch(pageTitle: string): Promise<string | any> {
        return getFormTitleAsync(this.translateService, pageTitle, null);
    }

    public async getFormTitleAsync(pageTitle: string, formMode: HvlFormConstants): Promise<string | any> {
        return getFormTitleAsync(this.translateService, pageTitle, formMode);
    }

    public getFormTitleSyncAsSearch(pageTitle: string): string | any {
        return getFormTitleSync(this.translateService, pageTitle, null);
    }

    public getFormTitleSync(pageTitle: string, formMode: HvlFormConstants): string | any {
        return getFormTitleSync(this.translateService, pageTitle, formMode);
    }

    public asItem(item: any): T {
        return item as T;
    }
}
